/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { ChangeEventHandler } from "react";
import { forwardRef, useRef, useState } from "react";
import type { DropEvent } from "react-aria";
import { useDrop } from "react-aria";

import type { ThemeType } from "../../../../global/emotion";
import { radius, spacing, stroke } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useLocalRef } from "../../../hooks/useLocalRef";
import type { HallowElementProps } from "../../../types/hallowElementProps.types";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";

const inputStyle: Interpolation = {
  display: "none",
};
const containerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "flex",
  gap: spacing.s,
  justifyContent: "center",
  paddingBlock: spacing.l,
  paddingInline: spacing.m,
});

type FileInputProps = Omit<
  HallowElementProps<"input">,
  "type" | "accept" | "ref"
> & {
  type: "csv";
  onFileChange: (f: File) => boolean | Promise<boolean>;
};

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  ({ type, onChange, onFileChange, className = "", ...props }, ref) => {
    const [value, setValue] = useState<File | null>(null);
    const localRef = useLocalRef<HTMLInputElement>(ref);
    const boxRef = useRef<HTMLDivElement>(null);
    const t = useTranslations();
    const alert = useAlert();
    const isDesktop = useIsDesktopViewport();
    const [loading, setLoading] = useState<boolean>(false);

    const handleFile = async (f: File) => {
      setLoading(true);
      try {
        const success = await onFileChange(f);
        if (success) setValue(f);
      } catch (e) {
        await alert.show({
          title: t("general_word_error"),
          description:
            e?.error?.message ??
            e?.error ??
            e?.message ??
            e ??
            t("general_error_message"),
        });
      }
      setLoading(false);
    };

    const handleDrop = async (dropEvent: DropEvent) => {
      for (const drop of dropEvent.items) {
        if (drop.kind === "file") {
          if (drop.type === "text/csv") {
            await handleFile(await drop.getFile());
            return;
          }
        }
      }
    };

    const handleChange: ChangeEventHandler<HTMLInputElement> = async (
      changeEvent,
    ) => {
      if (onChange) onChange(changeEvent);
      if (changeEvent.defaultPrevented) return;
      for (const f of Array.from(changeEvent.currentTarget.files)) {
        if (f.type === "text/csv") {
          await handleFile(f);
          break;
        }
      }
    };

    const { dropProps } = useDrop({
      ref: boxRef,
      isDisabled: !!value || loading,
      onDrop: handleDrop,
    });

    return (
      <div
        ref={boxRef}
        {...dropProps}
        css={containerStyle}
        className={className}
      >
        <input
          type="file"
          accept={`text/${type}`}
          ref={localRef}
          onChange={handleChange}
          disabled={loading}
          css={inputStyle}
          {...props}
        />
        {!!value ? (
          <Text>{value.name}</Text>
        ) : (
          <>
            {isDesktop && <Text>{t("admin_dash_drag_file")}</Text>}
            <Button
              onClick={() => localRef.current?.click()}
              size="s"
              disabled={loading}
            >
              {t("admin_dash_upload_file")}
            </Button>
          </>
        )}
      </div>
    );
  },
);

FileInput.displayName = "FileInput";
